import React from 'react';
import { format, parseISO, isValid } from 'date-fns';

export default function ConversationList({ 
  conversations, 
  selectedConversation,
  onSelectConversation,
  onNewConversation,
  loading 
}) {
  // Function to format timestamp
  const formatTime = (timestamp) => {
    try {
      if (!timestamp) return '';
      
      // If the timestamp is a string that looks like a message, return empty
      if (typeof timestamp === 'string' && timestamp.length > 20) {
        return '';
      }
      
      let date;
      
      // Handle different timestamp formats
      if (timestamp instanceof Date) {
        date = timestamp;
      } else if (timestamp?.toDate) {
        // Firestore timestamp
        date = timestamp.toDate();
      } else if (typeof timestamp === 'string') {
        // Try parsing ISO string
        date = new Date(timestamp);
      } else if (timestamp?.seconds) {
        // Firestore timestamp with seconds
        date = new Date(timestamp.seconds * 1000);
      } else {
        return '';
      }
      
      // Validate the date
      if (isNaN(date.getTime())) {
        return '';
      }
      
      const now = new Date();
      
      // If today, show time only
      if (date.toDateString() === now.toDateString()) {
        return format(date, 'h:mm a');
      }
      
      // If within the last week, show day name
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      if (date > oneWeekAgo) {
        return format(date, 'EEEE');
      }
      
      // Otherwise show date
      return format(date, 'MM/dd/yyyy');
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return '';
    }
  };

  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return 'New Conversation';
    
    // Strip any non-digit characters from the phone number
    const cleanNumber = phoneNumber.replace(/\D/g, '');
    
    // Format the number according to US convention if it's 10 digits
    if (cleanNumber.length === 10) {
      return `(${cleanNumber.substring(0, 3)}) ${cleanNumber.substring(3, 6)}-${cleanNumber.substring(6)}`;
    } else if (cleanNumber.length === 11 && cleanNumber.startsWith('1')) {
      // Format the number according to US convention with country code
      return `+1 (${cleanNumber.substring(1, 4)}) ${cleanNumber.substring(4, 7)}-${cleanNumber.substring(7)}`;
    }
    
    // Return the original format if it doesn't match known patterns
    return phoneNumber;
  };

  // Simple ellipsis function to limit characters
  const truncate = (str, n) => {
    if (!str) return '';
    return str.length > n ? str.substr(0, n-1) + '...' : str;
  };

  if (loading) {
    return (
      <div className="flex flex-col h-full">
        {/* Header Skeleton */}
        <div className="flex-none p-4 border-b border-white/10">
          <div className="flex justify-between items-center mb-2">
            <div className="h-6 bg-white/10 rounded w-32 animate-pulse"></div>
            <div className="w-9 h-9 bg-white/10 rounded-xl animate-pulse"></div>
          </div>
          <div className="mt-2 relative">
            <div className="h-8 bg-white/10 rounded-xl animate-pulse"></div>
          </div>
        </div>

        {/* Conversation List Skeleton */}
        <div className="flex-1 overflow-y-auto">
          <ul className="divide-y divide-white/10">
            {[1, 2, 3, 4, 5].map(i => (
              <li key={i} className="py-4 px-4 animate-pulse">
                <div className="flex justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="h-4 bg-white/10 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-white/10 rounded w-1/2"></div>
                  </div>
                  <div className="ml-2 flex-shrink-0 flex flex-col items-end">
                    <div className="h-3 bg-white/10 rounded w-12 mb-1"></div>
                    <div className="h-5 bg-white/10 rounded-full w-16"></div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header - Fixed at top */}
      <div className="flex-none p-4 border-b border-white/10">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-medium text-white">Conversations</h2>
          <button
            type="button"
            onClick={onNewConversation}
            className="inline-flex items-center justify-center rounded-xl p-2 text-white/60 hover:text-white hover:bg-white/10 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white/20 transition-all duration-200 border border-white/10"
            aria-label="New conversation"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
          </button>
        </div>
        <div className="mt-2 relative">
          <input
            type="text"
            placeholder="Search messages..."
            className="block w-full rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm py-1.5 pl-10 pr-3 text-white placeholder:text-white/40 focus:ring-1 focus:ring-inset focus:ring-indigo-500 focus:bg-white/10 sm:text-sm sm:leading-6 transition-all duration-200"
          />
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <svg className="h-5 w-5 text-white/40" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path
                fillRule="evenodd"
                d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Conversation List - Scrollable */}
      <div className="flex-1 overflow-y-auto">
        {conversations.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center p-4">
              <p className="text-white/60 mb-4">No conversations yet</p>
              <button
                onClick={onNewConversation}
                className="inline-flex items-center px-4 py-2 border border-white/10 text-sm font-medium rounded-xl shadow-sm text-white bg-indigo-600/80 backdrop-blur-sm hover:bg-indigo-600 hover:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200"
              >
                Start a conversation
              </button>
            </div>
          </div>
        ) : (
          <ul className="divide-y divide-white/10">
            {conversations.map((conversation) => {
              // Skip items without an aliasNumber - they're not valid
              if (!conversation.aliasNumber) return null;
              
              const isSelected = selectedConversation && 
                selectedConversation.aliasNumber === conversation.aliasNumber && 
                selectedConversation.lastContactNumber === conversation.lastContactNumber;
              
              // Create a unique key that handles null lastContactNumber
              const contactKey = conversation.lastContactNumber || 'new';
              const uniqueKey = `${conversation.aliasNumber}-${contactKey}`;
              
              return (
                <li 
                  key={uniqueKey}
                  className={`
                    relative py-4 px-4 cursor-pointer hover:bg-white/5 transition-all duration-200
                    ${isSelected ? 'bg-white/10 border-l-2 border-indigo-500' : ''}
                  `}
                  onClick={() => onSelectConversation(conversation)}
                >
                  <div className="flex justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">
                        {formatPhoneNumber(conversation.lastContactNumber)}
                      </p>
                      <p className="text-xs text-white/60 mt-1 truncate">
                        Via: {formatPhoneNumber(conversation.aliasNumber)}
                        {conversation.type && ` (${conversation.type})`}
                      </p>
                    </div>
                    <div className="ml-2 flex-shrink-0 flex flex-col items-end">
                      <p className="text-xs text-white/40">
                        {formatTime(conversation.lastMessage)}
                      </p>
                      
                      {/* Status badge */}
                      <div className="mt-1">
                        {conversation.connectionStatus?.toLowerCase() === 'active' ? (
                          <span className="inline-flex items-center rounded-full bg-emerald-400/20 px-2 py-1 text-xs font-medium text-emerald-400 ring-1 ring-inset ring-emerald-400/30">
                            Active
                          </span>
                        ) : (
                          <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ring-1 ring-inset ${
                            (conversation.connectionStatus || 'Ready') === 'Ready' 
                              ? 'bg-red-500/10 text-red-400 ring-red-500/20' 
                              : 'bg-white/10 text-white/60 ring-white/20'
                          }`}>
                            {(conversation.connectionStatus || 'Ready') === 'Ready' ? 'Inactive' : (conversation.connectionStatus || 'Ready')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Unread badge */}
                  {conversation.unread > 0 && (
                    <div className="absolute right-4 top-4">
                      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-xs font-medium text-white">
                        {conversation.unread}
                      </span>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
} 